import type { Language } from '@/types'
import i18n from 'i18next'
import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { isLanguage, stringToLang } from '@/i18n/language'
import { languageStorageKey } from '@/lib/client/storage'
import { isValidIdToken } from '@/lib/token'
import { atomWithLocalStorage, atomWithSessionStorage, parseStorageJSON } from '../storage'

export const idTokenAtom = atomWithLocalStorage<string | undefined>('idToken', undefined)

/**
 * The stored id token for code that runs outside the atom store, such as a route loader: the same
 * value `validIdTokenAtom` reads, and `undefined` once it has expired or is not there.
 */
export const readStoredIdToken = (): string | undefined => {
  try {
    const stored = parseStorageJSON(localStorage.getItem('idToken'))
    return typeof stored === 'string' && isValidIdToken(stored) ? stored : undefined
  } catch {
    return undefined
  }
}
export const tokenValidityRevisionAtom = atom(0)

const storedLanguageAtom = atomWithLocalStorage<Language>(languageStorageKey, 'fi', {
  getInitialValue: () => stringToLang(i18n.language),
  // The same rule the boot reader applies (readStoredLanguage), so a stored "sv" is Finnish in both.
  refine: (value) => (isLanguage(value) ? value : 'fi'),
})
export const languageAtom = atom(
  (get) => get(storedLanguageAtom),
  (get, set, value: Language | ((previous: Language) => Language)) => {
    const language = stringToLang(typeof value === 'function' ? value(get(storedLanguageAtom)) : value)
    set(storedLanguageAtom, language)
    if (i18n.language !== language) void i18n.changeLanguage(language)
    document.documentElement.lang = language
  }
)

export const spaAtom = atom(false)
export const loginPathAtom = atomWithSessionStorage<string | undefined>('loginPath', '/')

/** Used to force-refresh the user atom after mutations (e.g. updating own name). */
export const userRefreshAtom = atom(0)

export const openedEventAtom = atomFamily((eventId: string) =>
  atomWithSessionStorage(`open/eventId__${eventId}`, false)
)
